import React, { useContext, useEffect, useState } from 'react'

import { Box, Button, ButtonGroup, Card, Flex, Header, InputPartial, InputText, Link, styled, Tooltip } from '@driftt/dds'

import { CurrentUserContext } from 'context/currentUserContext'
import useNotifications from 'hooks/useNotifications'
import { segmentTrack,TrackType } from 'utils/tracking'

import { generateDemoData, sendDigestEmail } from './api'

const PageHeader = styled(Flex, {
    height: 80,
    width: '100%',
    paddingLeft: '$4',
    paddingRight: '$4',
    borderBottom: '1px solid $gray3',
    background: '$white',
})

const Container = styled(Box, {
width: '100%',
height: 'calc(100% - 80px)',
overflow: 'auto',
})

const Content = styled(Box, {
maxWidth: 1024,
margin: '$4 auto',
})

const ProspectorDemoControlsPage = () => {
    const currentUser = useContext(CurrentUserContext)
    const { success } = useNotifications()

    const emailErrorMessage = 'Contact email is a required field.'
    const domainErrorMessage = 'Account domain is a required field.'
    const [emailLoading, setEmailLoading] = useState<boolean>() 
    const [dataLoading, setDataLoading] = useState<boolean>() 
    const [accountDomain, setAccountDomain] = useState<string>()
    const [accountDomainError, setAccountDomainError] = useState<string>()
    const [contactEmail, setContactEmail] = useState<string>()
    const [contactEmailError, setContactEmailError] = useState<string>()


    useEffect(() => {
        segmentTrack(TrackType.DEMO_CONTROLS_PAGE_LOAD)
    },[])

    const sendEmail = () => {
        setEmailLoading(true)
        segmentTrack(TrackType.DEMO_CONTROLS_TRIGGER_DIGEST_EMAIL)
        sendDigestEmail()
            .then(() => {
                setEmailLoading(false)
                success('Email sent.')
            })
    }

    const setupDemo = () => {
        if(!accountDomain){
            setAccountDomainError(domainErrorMessage)
        }
        else if(!contactEmail){
            setContactEmailError(emailErrorMessage)
        } else {
            setDataLoading(true)
            segmentTrack(TrackType.DEMO_CONTROLS_SETUP_PERSONALIZED_DEMO)
            generateDemoData(`${currentUser?.orgId}_${accountDomain}`, `${contactEmail}@${accountDomain}`)
                .then(() => {
                    setDataLoading(false)
                    success('Demo is ready.')
                })
            setAccountDomain('')
            setContactEmail('')
        }
    }

    if (!currentUser) {
        return null
    }

    const emailTooltipContent = <>
        Will send your user a digest email according to your digest email preferences. 
        Those can be updated <Link variant="inline" size="small" href="https://app.drift.com/settings2/notifications">here</Link>.
    </>

    return (
        <div>
            <PageHeader alignItems='center' justifyContent='space-between'>
                <Header variant="h1">Prospector demo controls</Header>
                <Tooltip content={emailTooltipContent}>
                    <Button loading={emailLoading} onClick={sendEmail}>Trigger digest email</Button>
                </Tooltip>
                
            </PageHeader>
            <Container>
            <Content>
                <Card>
                    <Card.Header>
                        <Header variant="h3">Setup personalized demo</Header>
                    </Card.Header>
                    <Card.Content>
                        <Flex>
                            <InputText
                                errorMessage={accountDomainError}
                                value={accountDomain}
                                placeholder={'eg. visa.com'}
                                label="Enter account domain"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if(e.target.value.length > 0){
                                        setAccountDomainError('')
                                    } else {
                                        setAccountDomainError(domainErrorMessage)
                                    }
                                    setAccountDomain(e.target.value)
                                }}
                            />
                            <InputPartial
                                disabled={!accountDomain}
                                errorMessage={contactEmailError}
                                value={contactEmail}
                                placeholder={'eg. ptemplin'}
                                label="Enter contact email"
                                suffix={accountDomain && `@${accountDomain}`}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if(e.target.value.length > 0){
                                        setContactEmailError('')
                                    } else {
                                        setContactEmailError(emailErrorMessage)
                                    }
                                    setContactEmail(e.target.value)
                                }}
                            />
                        </Flex>
                        <ButtonGroup>
                            <Tooltip content="For the given account, this will clear out historical activity data 
                            and populate a pattern of buyer activity to make for a complete demo of Prospector. 
                            It will only impact the given account.">
                                <Button loading={dataLoading} onClick={setupDemo}>Submit</Button>
                            </Tooltip>
                        </ButtonGroup>
                    </Card.Content>
                </Card>   
            </Content>
            </Container>
        </div>
        
    )
}

export default ProspectorDemoControlsPage
